import { useState, useMemo } from 'react'
import { DashboardLayout } from '../../components/layout'
import { Button, StatCard, Modal } from '../../components/ui'
import { SubscriptionList, SubscriptionForm, BillingCalendar } from '../../features/subscriptions'
import { useSubscriptions } from '../../hooks/useSubscriptions'
import { useBusinesses } from '../../hooks/useBusinesses'
import { useToast } from '../../context/ToastContext'
import { formatCurrency } from '../../lib/format'
import LoadingSpinner from '../../components/ui/LoadingSpinner'

function SubscriptionsPage() {
  const {
    subscriptions,
    monthlyTotal,
    yearlyTotal,
    loading,
    createSubscription,
    updateSubscription,
    deleteSubscription,
    refetch,
  } = useSubscriptions()
  const { businesses } = useBusinesses()
  const toast = useToast()

  const [showModal, setShowModal] = useState(false)
  const [editItem, setEditItem] = useState(null)
  const [formLoading, setFormLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('list') // 'list' or 'calendar'
  const [filterBusinessId, setFilterBusinessId] = useState('all')

  const handleSubmit = async (formData) => {
    setFormLoading(true)
    const { error } = editItem
      ? await updateSubscription(editItem.id, formData)
      : await createSubscription(formData)
    setFormLoading(false)

    if (error) {
      toast.error(`Failed to ${editItem ? 'update' : 'add'} subscription`)
      return
    }

    toast.success(`Subscription ${editItem ? 'updated' : 'added'}`)
    setShowModal(false)
    setEditItem(null)
    refetch()
  }

  const handleDelete = async (subscriptionId) => {
    const { error } = await deleteSubscription(subscriptionId)
    if (error) {
      toast.error('Failed to delete subscription')
      return
    }
    toast.success('Subscription deleted')
    refetch()
  }

  const handleEdit = (subscription) => {
    setEditItem(subscription)
    setShowModal(true)
  }

  // Filter subscriptions based on selected business
  const filteredSubscriptions = useMemo(() => {
    if (filterBusinessId === 'all') return subscriptions
    if (filterBusinessId === 'ssv-all') {
      return subscriptions.filter(s => !s.business_id)
    }
    return subscriptions.filter(s => s.business_id === filterBusinessId)
  }, [subscriptions, filterBusinessId])

  // Calculate per-business breakdown
  const businessBreakdown = useMemo(() => {
    const breakdown = {}

    // Initialize with all businesses
    businesses.forEach(b => {
      breakdown[b.id] = {
        id: b.id,
        name: b.name,
        emoji: b.emoji,
        monthly: 0,
        count: 0,
      }
    })

    // Add "Second Shift Ventures All" category for shared/parent company subscriptions
    breakdown['ssv-all'] = {
      id: 'ssv-all',
      name: 'Second Shift Ventures All',
      emoji: '',
      monthly: 0,
      count: 0,
    }

    // Calculate totals
    subscriptions.forEach(sub => {
      if (!sub.is_active) return
      const amount = parseFloat(sub.amount) || 0
      const monthlyAmount = sub.frequency === 'yearly'
        ? amount / 12
        : sub.frequency === 'quarterly'
          ? amount / 3
          : amount

      const key = sub.business_id || 'ssv-all'
      if (breakdown[key]) {
        breakdown[key].monthly += monthlyAmount
        breakdown[key].count += 1
      }
    })

    // Convert to array and filter out empty ones, sort by monthly amount
    return Object.values(breakdown)
      .filter(b => b.count > 0)
      .sort((a, b) => b.monthly - a.monthly)
  }, [subscriptions, businesses])

  // Calculate filtered stats
  const filteredStats = useMemo(() => {
    const active = filteredSubscriptions.filter(s => s.is_active)

    const monthly = active.reduce((sum, sub) => {
      const amount = parseFloat(sub.amount) || 0
      if (sub.frequency === 'yearly') return sum + (amount / 12)
      if (sub.frequency === 'quarterly') return sum + (amount / 3)
      return sum + amount
    }, 0)

    const yearly = active.reduce((sum, sub) => {
      const amount = parseFloat(sub.amount) || 0
      if (sub.frequency === 'monthly') return sum + (amount * 12)
      if (sub.frequency === 'quarterly') return sum + (amount * 4)
      return sum + amount
    }, 0)

    // Calculate next 30 days billing
    const today = new Date()
    const next30Days = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000)
    const upcoming = active
      .filter(s => s.next_billing_date)
      .filter(s => {
        const billingDate = new Date(s.next_billing_date)
        return billingDate >= today && billingDate <= next30Days
      })
      .reduce((sum, s) => sum + parseFloat(s.amount), 0)

    return {
      monthly,
      yearly,
      activeCount: active.length,
      upcoming,
    }
  }, [filteredSubscriptions])

  if (loading) {
    return (
      <DashboardLayout title="Subscriptions">
        <LoadingSpinner.Overlay message="Loading subscriptions..." />
      </DashboardLayout>
    )
  }

  const filterOptions = [
    { value: 'all', label: 'All Businesses' },
    { value: 'ssv-all', label: 'Second Shift Ventures All' },
    ...businesses.map(b => ({ value: b.id, label: `${b.emoji} ${b.name}` })),
  ]

  return (
    <DashboardLayout
      title="Subscriptions"
      actions={
        <Button onClick={() => { setEditItem(null); setShowModal(true) }}>
          + Add Subscription
        </Button>
      }
    >
      {/* Business Filter */}
      <div className="mb-6">
        <select
          value={filterBusinessId}
          onChange={(e) => setFilterBusinessId(e.target.value)}
          className="form-select"
          style={{ maxWidth: '280px' }}
        >
          {filterOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-4 mb-8">
        <StatCard
          label="Monthly Total"
          value={formatCurrency(filteredStats.monthly)}
        />
        <StatCard
          label="Yearly Total"
          value={formatCurrency(filteredStats.yearly)}
        />
        <StatCard
          label="Active Subscriptions"
          value={filteredStats.activeCount}
        />
        <StatCard
          label="Due Next 30 Days"
          value={formatCurrency(filteredStats.upcoming)}
        />
      </div>

      {/* Per-Business Breakdown (only show when viewing all) */}
      {filterBusinessId === 'all' && businessBreakdown.length > 0 && (
        <div className="card mb-8">
          <div className="card-header">
            <h3 className="card-title">Expenses by Business</h3>
          </div>
          <div className="card-body p-0">
            <table className="table">
              <thead>
                <tr>
                  <th>Business</th>
                  <th className="text-right">Subscriptions</th>
                  <th className="text-right">Monthly Cost</th>
                  <th className="text-right">Yearly Cost</th>
                </tr>
              </thead>
              <tbody>
                {businessBreakdown.map(b => (
                  <tr
                    key={b.id}
                    onClick={() => setFilterBusinessId(b.id)}
                    style={{ cursor: 'pointer' }}
                    className="hover-row"
                  >
                    <td>
                      <span className="font-medium">
                        {b.emoji && `${b.emoji} `}{b.name}
                      </span>
                    </td>
                    <td className="text-right">{b.count}</td>
                    <td className="text-right">{formatCurrency(b.monthly)}</td>
                    <td className="text-right">{formatCurrency(b.monthly * 12)}</td>
                  </tr>
                ))}
                <tr className="font-bold" style={{ background: 'var(--gray-50)' }}>
                  <td>Total (All Businesses)</td>
                  <td className="text-right">
                    {businessBreakdown.reduce((sum, b) => sum + b.count, 0)}
                  </td>
                  <td className="text-right">{formatCurrency(monthlyTotal)}</td>
                  <td className="text-right">{formatCurrency(yearlyTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs mb-6">
        <button
          className={`tab ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          List View
        </button>
        <button
          className={`tab ${activeTab === 'calendar' ? 'active' : ''}`}
          onClick={() => setActiveTab('calendar')}
        >
          Billing Calendar
        </button>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'list' ? (
        <SubscriptionList
          subscriptions={filteredSubscriptions}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAdd={() => { setEditItem(null); setShowModal(true) }}
        />
      ) : (
        <BillingCalendar subscriptions={filteredSubscriptions} />
      )}

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditItem(null) }}
        title={editItem ? 'Edit Subscription' : 'Add Subscription'}
      >
        <SubscriptionForm
          initialData={editItem}
          businesses={businesses}
          onSubmit={handleSubmit}
          onCancel={() => { setShowModal(false); setEditItem(null) }}
          loading={formLoading}
        />
      </Modal>
    </DashboardLayout>
  )
}

export default SubscriptionsPage
